import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import PermissionsContainer from '../../components/PermissionsContainer';
import { TanTable } from '../../components/TanTable';
import { useToast } from '../../contexts/ToastContext';
import service, { type SageExportDetail, type SageExportRow } from '../../services/sage.service';
import { confirmColumns } from './sageConfirmColumns';
import { reportError, REPORT_BUTTON } from './reports/reportUtils';

/**
 * Confirm Export to Sage — the grid of exports, and the detail behind a row.
 *
 * Mark Confirmed is what stamps `masexportdate`, so it is the point of no return: until
 * it runs the records stay eligible and get exported again; once it runs they never
 * export again. The AR family additionally refuses while any record it covers is not
 * invoiced, and stamps nothing at all in that case.
 */
const DANGER = 'px-4 py-2 rounded bg-red-700 text-white hover:bg-red-600 '
  + 'disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium';

const SageConfirmCard: React.FC = () => {
  const { showToast } = useToast();
  const [rows, setRows] = useState<SageExportRow[]>([]);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);
  const [detail, setDetail] = useState<SageExportDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await service.listExports(includeDeleted));
    } catch (e) {
      showToast({ type: 'error', message: reportError(e, 'Could not load the Sage exports.') });
    } finally { setLoading(false); }
  }, [includeDeleted, showToast]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (selected === null) { setDetail(null); return; }
    let cancelled = false;
    service.getExport(selected)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch((e) => showToast({ type: 'error', message: reportError(e, 'Could not load that export.') }));
    return () => { cancelled = true; };
  }, [selected, showToast]);

  const confirm = async () => {
    if (!detail) return;
    setBusy(true);
    try {
      await service.markConfirmed(detail.recno);
      showToast({ type: 'success', message: `${detail.fileid} confirmed. Its records are now marked exported.` });
      setSelected(null);
      await load();
    } catch (e) {
      showToast({ type: 'error', message: reportError(e, 'Could not confirm that export.') });
    } finally { setBusy(false); }
  };

  const toggleDeleted = async () => {
    if (!detail) return;
    const next = detail.deleted === 'Y' ? 'N' : 'Y';
    setBusy(true);
    try {
      await service.setDeleted(detail.recno, next);
      // Re-read rather than patching `deleted` locally. Whether an export can still be
      // confirmed is the SERVER's rule (a deleted export cannot be), and a local patch
      // left `canConfirm` stale — so Mark Confirmed went on offering the point of no
      // return on an export that had just been deleted. The backend refused it, but the
      // button should not have been there. Found in UAT (Lee, 2026-09-17).
      setDetail(await service.getExport(detail.recno));
      await load();
    } catch (e) {
      showToast({ type: 'error', message: reportError(e, 'Could not save that change.') });
    } finally { setBusy(false); }
  };

  const download = async () => {
    if (!detail) return;
    try {
      const name = await service.downloadExport(detail.recno, detail.fileid);
      if (name) showToast({ type: 'success', message: `Saved ${name}` });
    } catch (e) {
      showToast({ type: 'error', message: reportError(e, 'Could not download that file.') });
    }
  };

  if (detail) {
    return (
      <div className="flex-1 min-h-0 p-4 space-y-4 text-label overflow-auto">
        <button type="button" onClick={() => setSelected(null)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline">
          <ArrowLeftIcon className="h-4 w-4" /> All exports
        </button>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <Field label="File Name" value={detail.fileid} mono />
          <Field label="Export Date" value={new Date(detail.exportdatetime).toLocaleString()} />
          <Field label="Total Records" value={detail.recordcount.toLocaleString()} />
          <Field label="Total Amount" value={detail.ttlamount} />
        </div>

        {detail.confirmed && (
          <div className="rounded border border-green-500 bg-green-50 dark:bg-green-950/30 p-2 text-sm">
            Confirmed {new Date(detail.confirmdatetime as string).toLocaleString()} — its records are
            marked exported and will not be exported again.
          </div>
        )}
        {detail.blockers.length > 0 && (
          <div className="rounded border border-red-400 bg-red-50 dark:bg-red-950/30 p-3 text-sm space-y-1">
            <div className="font-semibold text-red-700 dark:text-red-300">
              Cannot be confirmed yet — these are not invoiced:
            </div>
            {detail.blockers.map((b) => <div key={b} className="text-red-700 dark:text-red-300">{b}</div>)}
          </div>
        )}

        <div>
          <div className="text-sm mb-1">{detail.recordLabel} ({detail.records.length.toLocaleString()})</div>
          <textarea readOnly rows={12} value={detail.records.join('\n')}
            aria-label={detail.recordLabel}
            className="w-full rounded border border-theme-border-primary bg-theme-bg-secondary p-2 text-xs font-mono" />
        </div>

        <div className="flex gap-3 flex-wrap items-center">
          <button className={REPORT_BUTTON} onClick={download}>Download file</button>
          {detail.canSaveChanges && (
            <PermissionsContainer componentId="sage-export-savechanges"
              componentName="Sage Export: Save Changes" defaultHidden>
              <button className={REPORT_BUTTON} onClick={toggleDeleted} disabled={busy}>
                {detail.deleted === 'Y' ? 'Restore export' : 'Mark deleted'}
              </button>
            </PermissionsContainer>
          )}
          {detail.canConfirm && (
            // The point of no return: this stamps masexportdate, and a record that is
            // marked exported is never picked up by an export again.
            <PermissionsContainer componentId="sage-export-markconfirmed"
              componentName="Sage Export: Mark Confirmed" defaultHidden>
              <button className={DANGER} onClick={confirm} disabled={busy}>
                {busy ? 'Confirming…' : 'Mark Confirmed'}
              </button>
            </PermissionsContainer>
          )}
          {!detail.confirmed && !detail.canConfirm && detail.blockers.length === 0 && detail.deleted === 'Y' && (
            <span className="text-sm text-muted">A deleted export cannot be confirmed.</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 p-4 space-y-2 text-label">
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={includeDeleted}
          onChange={(e) => setIncludeDeleted(e.target.checked)} />
        Show deleted
      </label>
      <div className="flex-1 min-h-0">
        <TanTable<SageExportRow> settingsKey="accounting-sage-confirm" cardTitle="Confirm Export to Sage"
          data={rows} columns={confirmColumns} loading={loading} fillContainer enableColumnFilters
          onRowClick={(row) => setSelected(row.recno)} />
      </div>
    </div>
  );
};

const Field: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
  <div>
    <div className="text-xs text-muted">{label}</div>
    <div className={mono ? 'font-mono' : undefined}>{value}</div>
  </div>
);

export default SageConfirmCard;
